/**
 * Which steps of a chained run have nothing to do, and why. A step run on
 * its own always runs (the attorney asked for it); in Run Everything or Run
 * Remaining a step sits out only when the case shows its work is done and
 * nothing earlier in the run changed what it builds on. Pure so the rules
 * are testable.
 */
import { formsCurrent } from "./formsStatus";
import type { ResearchTopic, ResearchTopicsState } from "./researchTopics";
import { derived, retitleCurrent, type WorkflowFacts } from "./status";
import type {
  FormChoice,
  LetterChoice,
  WorkflowId,
  WorkflowRunRecord,
  WorkflowScope
} from "./types";

export type SkipInput = {
  facts: WorkflowFacts;
  records: Partial<Record<WorkflowId, WorkflowRunRecord>>;
  scope: WorkflowScope;
  chained: boolean;
  /** Steps of this run that did work (ran and finished, not skipped). */
  worked: ReadonlySet<WorkflowId>;
  /** Draft Letters' letters for this run. */
  letters: LetterChoice[];
  /** Fill Forms' copies for this run; absent leaves the forms to the visa. */
  forms?: FormChoice[];
  /** The draft types the case has a letter of. */
  draftTypes: ReadonlySet<string>;
  research: ResearchTopicsState;
  topics: ResearchTopic[];
};

/**
 * Why a step sits out. `upToDate`: its work is done, so the card keeps its
 * own status with this as the detail; otherwise it reads Skipped.
 */
export type Skip = { detail: string; upToDate: boolean };

const upToDate = (detail: string): Skip => ({ detail, upToDate: true });

/** Why Case Research sits out a run, or null to run it. Applies to every run. */
export const researchSkip = (
  state: ResearchTopicsState,
  topics: ResearchTopic[]
): Skip | null => {
  if (state.kind === "unavailable")
    return {
      detail: "Skipped: Research isn't part of the firm's plan",
      upToDate: false
    };
  if (state.kind === "ready" && !topics.length)
    return state.topics.some((t) => t.status === "done")
      ? upToDate("Skipped: Every topic's research is current")
      : { detail: "Skipped: No research topics picked", upToDate: false };
  return null;
};

const workedAny = (worked: ReadonlySet<WorkflowId>, ids: WorkflowId[]) =>
  ids.some((id) => worked.has(id));

/** Why a step sits out this run, or null to run it. */
export const skipReason = (id: WorkflowId, input: SkipInput): Skip | null => {
  if (id === "research") return researchSkip(input.research, input.topics);
  if (!input.chained || input.scope.kind !== "default") return null;
  const { facts, records, worked } = input;
  const done = derived(id, facts, records).status === "done";
  switch (id) {
    case "organize":
      // Only Casey's own organize counts: folders made by hand may not be the firm's.
      return done && records.organize?.status === "done"
        ? upToDate(
            "Skipped: Every file is in a folder, and none was added since the last organize"
          )
        : null;
    case "insights":
      // A review just after Organize sees the values that pass writes.
      if (workedAny(worked, ["organize"])) return null;
      return done && records.insights?.status === "done"
        ? upToDate(
            "Skipped: Case Insights were proposed from the files, and no file was added since"
          )
        : null;
    case "inconsistencies":
      if (workedAny(worked, ["organize", "insights"])) return null;
      return done && records.inconsistencies?.status === "done"
        ? upToDate(
            "Skipped: The record was consistent, and no file was added since"
          )
        : null;
    case "retitle":
      return retitleCurrent(facts, records.retitle)
        ? upToDate(
            "Skipped: The last retitle covered every file with today's title settings, and no file was added or renamed since"
          )
        : null;
    case "number":
      return facts.styleSettled
        ? upToDate(
            `Skipped: The case already has its label style${
              facts.labelStyle ? ` (${facts.labelStyle})` : ""
            }`
          )
        : null;
    case "letters": {
      if (!done || !input.letters.length) return null;
      if (workedAny(worked, ["organize", "retitle", "number"])) return null;
      const missing = input.letters.some(
        (l) => !input.draftTypes.has(l.draftType)
      );
      return missing
        ? null
        : upToDate("Skipped: Every letter is drafted and current");
    }
    case "forms":
      // Done reads each form's answers: a form the case was created with, or
      // one whose fill failed or filled nothing, always runs, and so does a
      // picked copy the case doesn't have yet.
      if (
        input.forms?.some(
          (f) =>
            !f.formId || !facts.forms.some((i) => i.artifact.id === f.formId)
        )
      )
        return null;
      return done && formsCurrent(facts.forms, facts.stamps)
        ? upToDate("Skipped: Every form is filled, and no file was added since")
        : null;
    case "packet":
      return done &&
        !workedAny(worked, [
          "organize",
          "retitle",
          "number",
          "letters",
          "forms"
        ])
        ? upToDate("Skipped: The packet is current")
        : null;
    default:
      return null;
  }
};
